/**
 * «Sunucu render» — `renderServerPreview` (`m08-admin.js`).
 *
 * The panel under the stage that renders the UNSAVED draft the way the server would: it resolves
 * the same fail-closed render plan, rasterises it to a PNG, and offers the canonical SVG and the
 * layout JSON the plan came from. Nothing here is a mock of a server call — upstream has no server
 * either, and the strings it labels the output with ("Sunucu render simülasyonu", and the note
 * that `@resvg/resvg-js` turns this SVG into the PNG in production) are reproduced as written.
 * They are the reference's own claim about what this is, and softening or extending them would be
 * inventing product copy.
 *
 * The panel lives in `#renderOut.render-out`, which the reference's markup keeps in the DOM at all
 * times and only toggles `hidden` on — so this component renders that container itself, hidden
 * until the button has been pressed, rather than being mounted and unmounted around it.
 */
import { type ReactElement, useEffect, useState } from 'react';
import type { DemoTemplate } from '../../data/types';
import {
  type BoxGeo,
  canvasH,
  canvasW,
  fontStr,
  type Layout,
  type Placement,
  type PlanFailure,
  planToSVG,
  type RenderData,
  type RenderPlan,
  resolveRenderPlan,
  type TextGeo,
  watermarkSize,
} from '../../lib/render';
import type { EditorLayout } from './contract';

// ------------------------------------------------------------------------ Engine.planToCanvas

/**
 * `m04-engine.js` §planToCanvas, ported here rather than into `lib/render/engine.ts`.
 *
 * Every other surface in the demo draws SVG, so the engine module deliberately ports only the SVG
 * renderer. This panel is the one place the reference rasterises, because the artifact it hands
 * the operator is a PNG — and the download has to be the same pixels the panel shows. The port is
 * unabridged, including the watermark branch this call site never asks for: it is upstream's
 * rasteriser, not a subset of it.
 */
interface CanvasOptions {
  white?: boolean;
  watermark?: string;
}

/** Canvas 2D has no letter-spacing, so a spaced line is drawn glyph by glyph, n−1 gaps. */
function drawSpaced(
  g: CanvasRenderingContext2D,
  text: string,
  x: number,
  baseline: number,
  align: CanvasTextAlign,
  ls: number | undefined,
): void {
  if (!ls) {
    g.textAlign = align;
    g.fillText(text, x, baseline);
    return;
  }
  const total = Array.from(text).reduce((a, ch) => a + g.measureText(ch).width, 0) + ls * (text.length - 1);
  let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
  g.textAlign = 'left';
  for (const ch of Array.from(text)) {
    g.fillText(ch, cx, baseline);
    cx += g.measureText(ch).width + ls;
  }
}

/** A failed image resolves to null instead of rejecting, so one broken asset cannot stop the draw. */
const loadImg = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((res) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = src;
  });

async function planToCanvas(
  plan: RenderPlan,
  scale = 1,
  opts: CanvasOptions = {},
): Promise<HTMLCanvasElement> {
  const W = plan.canvas.width;
  const H = plan.canvas.height;
  const c = document.createElement('canvas');
  c.width = Math.round(W * scale);
  c.height = Math.round(H * scale);
  /* `getContext` is nullable only for an element that already holds a different context kind; a
     canvas created one line above always yields one, and upstream does not guard either. */
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  g.save();
  g.scale(scale, scale);
  if (opts.white !== false) {
    g.fillStyle = '#FFFFFF';
    g.fillRect(0, 0, W, H);
  }
  const drawPlaced = async (p: Placement, dataUrl: string): Promise<void> => {
    const im = await loadImg(dataUrl);
    if (!im) return;
    g.save();
    g.globalAlpha = p.opacity;
    if (p.clip) {
      g.beginPath();
      g.rect(p.clip.x, p.clip.y, p.clip.w, p.clip.h);
      g.clip();
    }
    if (p.rotation) {
      g.translate(p.pivot.x, p.pivot.y);
      g.rotate((p.rotation * Math.PI) / 180);
      g.translate(-p.pivot.x, -p.pivot.y);
    }
    g.drawImage(im, p.sx, p.sy, p.sw, p.sh, p.dx, p.dy, p.dw, p.dh);
    g.restore();
  };
  if (plan.background) await drawPlaced(plan.background.placement, plan.background.dataUrl);
  for (const it of plan.items) {
    if (it.kind === 'image') {
      if (!it.missing) await drawPlaced(it.placement as Placement, it.dataUrl as string);
      continue;
    }
    const l = it.layer;
    const box = it.geo as BoxGeo;
    const txt = it.geo as TextGeo;
    g.save();
    g.globalAlpha = it.opacity as number;
    if (it.rotation) {
      /* A QR turns about the centre of its square, a text block about its own middle. */
      const px = it.kind === 'qr' ? box.x + box.size / 2 : txt.x;
      const py = it.kind === 'qr' ? box.y + box.size / 2 : txt.top + txt.blockH / 2;
      g.translate(px, py);
      g.rotate((it.rotation * Math.PI) / 180);
      g.translate(-px, -py);
    }
    if (it.kind === 'qr') {
      g.fillStyle = '#fff';
      g.fillRect(box.x, box.y, box.size, box.size);
      if (it.matrix) {
        const n = it.matrix.length;
        /* Two modules of quiet zone on each side, so the code occupies size × n/(n+4). */
        const cell = box.size / (n + 4);
        g.fillStyle = l.color || '#000';
        for (let rr = 0; rr < n; rr++) {
          for (let cc = 0; cc < n; cc++) {
            /* The .2 overdraw closes the hairline seams device-pixel rounding leaves between cells. */
            if ((it.matrix[rr] as boolean[])[cc]) {
              g.fillRect(box.x + (2 + cc) * cell, box.y + (2 + rr) * cell, cell + 0.2, cell + 0.2);
            }
          }
        }
      } else {
        g.strokeStyle = '#8B837C';
        g.setLineDash([6, 6]);
        g.strokeRect(box.x, box.y, box.size, box.size);
        g.setLineDash([]);
      }
    } else {
      g.font = fontStr(l, it.size as number);
      g.fillStyle = l.color as string;
      g.textBaseline = 'alphabetic';
      for (const [i, ln] of (it.lines as string[]).entries()) {
        drawSpaced(
          g,
          ln,
          txt.x,
          txt.baselines[i] as number,
          l.align === 'left' ? 'left' : l.align === 'right' ? 'right' : 'center',
          it.letterSpacingPx,
        );
      }
    }
    g.restore();
  }
  if (opts.watermark) {
    const fs = watermarkSize(plan.canvas, opts.watermark);
    g.save();
    g.translate(W / 2, H / 2);
    g.rotate((-24 * Math.PI) / 180);
    g.fillStyle = 'rgba(207,5,30,.86)';
    g.fillRect(-W * 0.8, -fs * 0.95, W * 1.6, fs * 1.9);
    g.fillStyle = '#fff';
    g.font = `700 ${fs}px Poppins, sans-serif`;
    g.textAlign = 'center';
    g.fillText(opts.watermark, 0, fs * 0.35);
    g.restore();
  }
  g.restore();
  return c;
}

// ---------------------------------------------------------------------------- Engine.layoutJSON

/**
 * `m04-engine.js` §layoutJSON — the «Layout JSON» view.
 *
 * It is the draft plus the plan the draft resolves to, which is what makes it worth showing: the
 * geometry the server would compute is in the same document as the layout it was computed from.
 * The plan is resolved with EMPTY data on purpose, so the JSON describes the template rather than
 * whatever happens to be in the test-data fields.
 */
function layoutJSON(layout: EditorLayout, meta: DemoTemplate): string {
  const plan = resolveRenderPlan(layout as unknown as Layout, {}, {});
  return JSON.stringify(
    {
      template: meta.name,
      credential_type: layout.credentialType,
      subject_type: layout.subjectType,
      canvas: layout.canvas,
      background: layout.background,
      publication_policy: layout.publicationPolicy,
      field_definitions: (layout.fieldDefinitions || []).map((f) => ({
        key: f.key,
        data_type: f.dataType,
        required: f.required,
        importable: f.importable,
        visibility: f.visibility,
        auto: f.auto,
        validation: f.validation,
      })),
      safe_areas: layout.safeAreas,
      layers: (layout.layers || []).map((l) => ({ ...l })),
      render_plan: {
        background: plan.background ? plan.background.placement : null,
        items: plan.items.map((i) => ({
          kind: i.kind,
          field: i.layer.fieldKey,
          placement: i.placement || null,
          geo: i.geo || null,
        })),
      },
    },
    null,
    2,
  );
}

/**
 * `downloadDataUrl` (`m01-utils.js`), minus its artifact-host branch: upstream first asks the
 * published-artifact host for a `downloads` capability and falls back to `<a download>`. This
 * build is a plain page, so only the fallback can apply — the same shape `downloadText` in
 * `lib/asset-library.ts` already uses.
 */
function downloadDataUrl(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ------------------------------------------------------------------------------------ the panel

type Output =
  | { phase: 'busy' }
  | { phase: 'invalid'; failures: PlanFailure[] }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; plan: RenderPlan; png: string; svg: string; json: string };

export interface ServerRenderOutProps {
  template: DemoTemplate;
  /** `ED.layout` — the unsaved working draft, which is the whole point of the panel. */
  layout: EditorLayout;
  /** `previewData()` — the editor's test data, with `verification_url` already resolved. */
  data: RenderData;
  /**
   * One press of «Sunucu render». Upstream re-runs `renderServerPreview` on every click and sets
   * `$('#renderOut').hidden = true` when the editor loads another template; here 0 means "not
   * pressed" (the container stays `hidden`, as the reference's own DOM has it) and each increment
   * is one run.
   */
  runId: number;
  /**
   * `FontState.ready`. Both seeded Poppins faces are registered from the reference's own bytes, so
   * this surface is ready for the same reason the prototype is — pass false once an uploaded face
   * has failed to load, and the reference's warning about a non-canonical render appears.
   */
  fontsReady?: boolean | undefined;
}

export function ServerRenderOut({
  template,
  layout,
  data,
  runId,
  fontsReady = true,
}: ServerRenderOutProps): ReactElement {
  const [out, setOut] = useState<Output | null>(null);
  const [code, setCode] = useState<'svg' | 'json' | null>(null);

  /* Upstream renders the draft AS IT WAS when the button was pressed and leaves that output on
     screen until the next press, so the run is keyed on the press alone; re-running it whenever
     `layout` or `data` changes would turn a deliberate, expensive rasterisation into a live
     preview the reference does not have. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the press is the trigger, see above
  useEffect(() => {
    if (runId <= 0) {
      setOut(null);
      setCode(null);
      return;
    }
    let cancelled = false;
    setOut({ phase: 'busy' });
    setCode(null);
    const lay = layout as unknown as Layout;
    void (async () => {
      try {
        const plan = resolveRenderPlan(lay, data, {
          width: canvasW(lay.canvas),
          height: canvasH(lay.canvas),
        });
        if (!plan.ok) {
          if (!cancelled) setOut({ phase: 'invalid', failures: plan.failures });
          return;
        }
        const c = await planToCanvas(plan, 1);
        const png = c.toDataURL('image/png');
        const svg = planToSVG(plan, {});
        if (!cancelled) setOut({ phase: 'ready', plan, png, svg, json: layoutJSON(layout, template) });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (!cancelled) setOut({ phase: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  let body: ReactElement | null = null;

  if (out?.phase === 'busy') {
    body = (
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }} className="muted">
        <span className="spinner" />
        {' Canonical SVG oluşturuluyor ve PNG render ediliyor…'}
      </div>
    );
  } else if (out?.phase === 'invalid') {
    body = (
      <div className="warn-box">
        <b>Render planı doğrulanamadı (fail-closed).</b>
        <ul style={{ margin: '6px 0 0 18px' }}>
          {out.failures.map((f, i) => {
            /* A failure carries its plan-failure CODE in `reason`, which is what the reference
               prints here — the Turkish sentence belongs to the stage's own fit notice. */
            const assetId = typeof f.assetId === 'string' ? f.assetId : '';
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: failures have no id and are rebuilt whole
              <li key={`${f.code}-${i}`}>{`${f.where}: ${f.reason}${assetId ? ` · ${assetId}` : ''}`}</li>
            );
          })}
        </ul>
      </div>
    );
  } else if (out?.phase === 'error') {
    body = <div className="warn-box">{`Render başarısız: ${out.message}`}</div>;
  } else if (out?.phase === 'ready') {
    const { plan, png, svg, json } = out;
    body = (
      <>
        {fontsReady ? null : (
          <div className="warn-box">
            Fontlar tam doğrulanmadı (asset digest'i eksik veya fallback). Bu önizleme canonical render
            sayılmaz ve yayın kapısı bu durumda release üretmez.
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          {/* The plan's canvas is the layout's canvas at the moment of the press, so the caption
              keeps describing the artifact below it even if the draft is resized afterwards. */}
          <span className="eyebrow">
            {`Sunucu render simülasyonu · ${plan.canvas.width}×${plan.canvas.height} PNG`}
          </span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="dlPng"
              onClick={() => downloadDataUrl(png, `${template.id}-preview.png`)}
            >
              PNG indir
            </button>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="dlPng2"
              onClick={() => {
                void planToCanvas(plan, 2).then((c2) =>
                  downloadDataUrl(c2.toDataURL('image/png'), `${template.id}-preview@2x.png`),
                );
              }}
            >
              2× PNG indir
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              id="showSvg"
              onClick={() => setCode((c) => (c === 'svg' ? null : 'svg'))}
            >
              Canonical SVG
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              id="showJson"
              onClick={() => setCode((c) => (c === 'json' ? null : 'json'))}
            >
              Layout JSON
            </button>
          </div>
        </div>
        <img src={png} alt="Render önizlemesi" />
        <div className="help">
          {'Aynı geometri ve aynı font dosyasıyla üretimde '}
          <code>@resvg/resvg-js</code>
          {" bu SVG'yi PNG'ye çevirir."}
        </div>
        {/* Both views stay in the DOM and are toggled by `hidden`, as the reference toggles them. */}
        <pre className="code-view" id="codeSvg" hidden={code !== 'svg'}>
          {svg}
        </pre>
        <pre className="code-view" id="codeJson" hidden={code !== 'json'}>
          {json}
        </pre>
      </>
    );
  }

  return (
    <div className="render-out" id="renderOut" hidden={out === null}>
      {body}
    </div>
  );
}
